/** Simulation scenarios — lifted from DNSLeakTest.dc.html so the default
 *  (backend-less) build is a faithful, demoable reproduction of the mockup.
 *  In `live` mode these are unused; real data comes from the API + SSE. */
import type { Badge, Nameserver, Relation, Verdict } from "./types";

export interface ScenarioServer {
  ip: string;
  hostname: string;
  relation: Relation;
  badge: Badge;
  publicName?: string;
  knownIsp?: string;
  country: string;
  cc: string;
  flag: string;
  leak: boolean;
  /** Lookup index at which this resolver is revealed during the animation. */
  at: number;
}

export interface Scenario {
  ip: string;
  rdns: string;
  city: string;
  region: string;
  country: string;
  flag: string;
  cc: string;
  postal: string;
  operator: string;
  asn: string;
  lon: number;
  lat: number;
  servers: ScenarioServer[];
  /** Every Nth simulated lookup travels the leaked (red) path; 0 = none. */
  leakEvery: number;
  strangerHost?: string;
  isExit: boolean;
}

export type ScenarioKey = "secure" | "leak" | "novpn";

const DE = "🇩🇪";
const US = "🇺🇸";

export const SCENARIOS: Record<ScenarioKey, Scenario> = {
  secure: {
    ip: "185.220.101.34", rdns: "exit34.limburg.rapidtunnel.com", city: "Limburg", region: "Hesse",
    country: "Germany", flag: DE, cc: "DE", postal: "65549", operator: "RapidTunnel", asn: "AS208294",
    lon: 8.06, lat: 50.386, isExit: true, leakEvery: 0,
    servers: [
      { ip: "185.220.101.53", hostname: "dns1.rapidtunnel.com", relation: "rapidtunnel", badge: "RAPIDTUNNEL", country: "Germany", cc: "DE", flag: DE, leak: false, at: 1 },
      { ip: "185.220.101.54", hostname: "dns2.rapidtunnel.com", relation: "rapidtunnel", badge: "RAPIDTUNNEL", country: "Germany", cc: "DE", flag: DE, leak: false, at: 5 },
    ],
  },
  leak: {
    ip: "185.220.101.34", rdns: "exit34.limburg.rapidtunnel.com", city: "Limburg", region: "Hesse",
    country: "Germany", flag: DE, cc: "DE", postal: "65549", operator: "RapidTunnel", asn: "AS208294",
    lon: 8.06, lat: 50.386, isExit: true, leakEvery: 3, strangerHost: "resolver.comnet.example",
    servers: [
      { ip: "185.220.101.53", hostname: "dns1.rapidtunnel.com", relation: "rapidtunnel", badge: "RAPIDTUNNEL", country: "Germany", cc: "DE", flag: DE, leak: false, at: 1 },
      { ip: "92.168.44.10", hostname: "resolver1.comnet.example", relation: "other", badge: "OTHER NETWORK", knownIsp: "ComNet Broadband", country: "United States", cc: "US", flag: US, leak: true, at: 3 },
      { ip: "92.168.44.11", hostname: "resolver2.comnet.example", relation: "other", badge: "OTHER NETWORK", knownIsp: "ComNet Broadband", country: "United States", cc: "US", flag: US, leak: true, at: 6 },
    ],
  },
  novpn: {
    ip: "5.45.107.96", rdns: "v2202605362763464550.ultrasrv.de", city: "Nuremberg", region: "Bavaria",
    country: "Germany", flag: DE, cc: "DE", postal: "90403", operator: "netcup GmbH", asn: "AS197540",
    lon: 11.077, lat: 49.452, isExit: false, leakEvery: 1, strangerHost: "edge.cloudflare.com",
    servers: [
      { ip: "162.158.93.159", hostname: "edge159.cloudflare.com", relation: "public", badge: "PUBLIC DNS", publicName: "Cloudflare", country: "United States", cc: "US", flag: US, leak: true, at: 1 },
      { ip: "162.158.93.160", hostname: "edge160.cloudflare.com", relation: "public", badge: "PUBLIC DNS", publicName: "Cloudflare", country: "United States", cc: "US", flag: US, leak: true, at: 4 },
      { ip: "162.158.93.161", hostname: "edge161.cloudflare.com", relation: "public", badge: "PUBLIC DNS", publicName: "Cloudflare", country: "United States", cc: "US", flag: US, leak: true, at: 7 },
    ],
  },
};

export const scenarioVerdict: Record<ScenarioKey, Verdict> = {
  secure: "secure",
  leak: "external",
  novpn: "consistent",
};

export const DOMAINS = [
  "cdn.example.com", "ads.tracker.example", "api.github.com", "news.ycombinator.com",
  "stream.video.example", "img.social.example", "shop.store.example", "maps.osm.org",
  "chat.signal.org", "mail.proton.example", "files.drop.example", "search.duck.example",
];

/** Simulated nameserver locations + latencies for the design scenarios. */
export const SIM_NAMESERVERS: Nameserver[] = [
  { id: "fra", label: "Frankfurt, DE", ping: "" },
  { id: "iad", label: "Ashburn, US", ping: "" },
  { id: "sin", label: "Singapore", ping: "" },
];
export const SIM_PINGS: Record<string, number> = { fra: 18, iad: 94, sin: 171 };
